import { DISCOUNT_SOURCE, resolveDiscount } from './discountCalculation';
import { NumericEngine, Raw } from './numericEngine';

export const DOCUMENT_TAX_TYPE = {
    INVOICE: 'invoice',
    ESTIMATE: 'estimate',
    WORK_ORDER: 'workOrder',
};

export const DEFAULT_TAX_DECIMAL_PRECISION = 2;

/**
 * Allowed decimal places for tax percentages, from shop config (default 2).
 * config.taxDecimalPrecision.upTo
 */
export const getTaxDecimalPrecision = (shopConfigData) => {
    const upTo = Number(shopConfigData?.config?.taxDecimalPrecision?.upTo);
    return Number.isFinite(upTo) && upTo > 0 ? upTo : DEFAULT_TAX_DECIMAL_PRECISION;
};

/**
 * Display a tax percentage rounded to at most `precision` decimals, no trailing zeros.
 * 5.712 @2 -> "5.71" ; 5.712 @3 -> "5.712" ; 5.7 @3 -> "5.7"
 */
export const formatTaxPercent = (value, precision = DEFAULT_TAX_DECIMAL_PRECISION) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    return NumericEngine.format(n, { precision, trailingZeros: false });
};

/**
 * Input mask for a tax-percent field allowing up to `precision` decimals.
 */
export const taxPercentInputRegex = (precision = DEFAULT_TAX_DECIMAL_PRECISION) => new RegExp(`^\\d*(\\.\\d{0,${precision}})?$`);

/**
 * The rate field each document reads off a shop tax rule. A work order has its own rate key even
 * though Account Settings writes the same value into all three (see SalesTax.jsx).
 */
export const RATE_KEY = {
    [DOCUMENT_TAX_TYPE.INVOICE]: 'invoiceRate',
    [DOCUMENT_TAX_TYPE.ESTIMATE]: 'estimateRate',
    [DOCUMENT_TAX_TYPE.WORK_ORDER]: 'workOrderAuthorizedRate',
};

/** The "tax on by default" flag each document reads off a shop tax rule. */
const INCLUDE_KEY = {
    [DOCUMENT_TAX_TYPE.INVOICE]: 'includeInvoiceTax',
    [DOCUMENT_TAX_TYPE.ESTIMATE]: 'includeEstimateTax',
    [DOCUMENT_TAX_TYPE.WORK_ORDER]: 'includeWorkOrderAuthorizedTax',
};

/** Rate on a row whatever document wrote it — used for fallbacks and display. */
export const anyRowRate = (row) => row?.invoiceRate ?? row?.estimateRate ?? row?.workOrderAuthorizedRate;

/**
 * Unwrap shop taxes API payload ({ json: { taxRules, ... } } or flat document).
 */
export const normalizeShopTaxesData = (shopTaxesData) => {
    if (!shopTaxesData) return null;
    const doc = shopTaxesData?.json ?? shopTaxesData?.data ?? shopTaxesData;
    if (!doc || typeof doc !== 'object') return null;
    return doc;
};

/**
 * Get taxRules array from shop taxes response (handles json wrapper).
 */
export const getShopTaxRules = (shopTaxesData) => normalizeShopTaxesData(shopTaxesData)?.taxRules || [];

/**
 * New tax version when GET response includes a NON-EMPTY taxesBreakdown array.
 * Legacy invoices/estimates may echo back `taxesBreakdown: []` after update — an empty
 * array must stay legacy (old UI + single-tax calc), not flip to the new multi-tax UI.
 */
export const isNewTaxVersion = (record) => Array.isArray(record?.taxesBreakdown) && record.taxesBreakdown.length > 0;

/**
 * True when the shop's tax settings were edited AFTER the record last saved its tax.
 *
 * The shop-taxes document carries a single `updatedAt`; each invoice/estimate carries its own.
 * Editing a record's OWN taxes never bumps the ACCOUNT timestamp, so a genuine per-line
 * customization is preserved — only a real account-side change trips a later reset.
 *
 * Missing either timestamp → false (treat as unchanged): the safe default so a not-yet-loaded
 * shopTaxes never triggers a surprise reset on a race.
 */
export const isTaxSettingsChangedSince = (shopTaxesData, recordUpdatedAt) => {
    const shopUpdatedAt = shopTaxesData?.updatedAt ?? normalizeShopTaxesData(shopTaxesData)?.updatedAt;
    if (!shopUpdatedAt || !recordUpdatedAt) return false;
    return new Date(shopUpdatedAt).getTime() > new Date(recordUpdatedAt).getTime();
};

/**
 * THE single decision for "does this record keep its own per-line + global taxes, or reset to the
 * current account defaults?" — shared by estimate edit, estimate duplicate, invoice edit and
 * estimate→invoice conversion so every flow decides identically.
 *
 * preserveTax = the record was saved under the NEW tax system AND the account tax settings have not
 * changed since. Anything else — a legacy record, or account settings edited since — resets.
 */
export const resolveTaxPreservation = (record, shopTaxesData) => {
    const sourceIsNew = isNewTaxVersion(record);
    const taxSettingsChanged = isTaxSettingsChangedSince(shopTaxesData, record?.updatedAt);
    return { sourceIsNew, taxSettingsChanged, preserveTax: sourceIsNew && !taxSettingsChanged };
};

/**
 * Cross-document global tax rows for a conversion. Rates + appliesTo flags come from the TARGET
 * document's account defaults (a converted document is an invoice — or a work order — now, so it
 * uses that document's rate key); the per-tax applyTax (the summary checkbox on/off state the user
 * set on the source) is overlaid by taxId. Rules the source never carried stay at their target
 * default; rules it carried that no longer exist on the shop are dropped.
 */
export const buildInvoiceBreakdownFromEstimate = (estimateGlobalRows = [], shopTaxesData, documentType = DOCUMENT_TAX_TYPE.INVOICE) => {
    const targetDefaults = buildTaxesBreakdownFromShopTaxes(shopTaxesData, documentType);
    const estMap = new Map((estimateGlobalRows || []).map((r) => [r.taxId, r]));
    return targetDefaults.map((row) => {
        const e = estMap.get(row.taxId);
        return e ? { ...row, applyTax: e.applyTax !== false } : row;
    });
};

/**
 * Normalize a taxesBreakdown array from the GET response into the internal shape.
 * Backend returns top-level rows as { taxName, taxPercentage, taxAmount, applyTax } and
 * per-line rows as { invoiceRate|estimateRate, applyTax, taxAmount } (no name). This maps
 * name <- taxName and [rateKey] <- (existing rate ?? taxPercentage) so every reader
 * (edit hydration, payment summary, MultiTaxRows, LineTaxCell) finds name + rate consistently.
 */
export const normalizeTaxesBreakdownFromResponse = (taxesBreakdown = [], documentType = DOCUMENT_TAX_TYPE.INVOICE) => {
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    return (Array.isArray(taxesBreakdown) ? taxesBreakdown : []).map((t) => ({
        ...t,
        name: t.name ?? t.taxName ?? '',
        taxName: t.taxName ?? t.name ?? '',
        [rateKey]: Number(t[rateKey] ?? t.taxPercentage) || 0,
    }));
};

/**
 * Build initial taxesBreakdown from shopTaxes/list for new create.
 */
export const buildTaxesBreakdownFromShopTaxes = (shopTaxesData, documentType = DOCUMENT_TAX_TYPE.INVOICE) => {
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    const includeKey = INCLUDE_KEY[documentType] || INCLUDE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    const rules = getShopTaxRules(shopTaxesData);

    return rules.map((rule) => ({
        taxId: rule._id,
        name: rule.taxName,
        applyTax: rule[includeKey] ?? true,
        [rateKey]: Number(rule[rateKey] ?? anyRowRate(rule)) || 0,
        appliesToParts: rule.appliesToParts ?? true,
        appliesToLabour: rule.appliesToLabour ?? true,
        appliesToServicePackage: rule.appliesToServicePackage ?? true,
        appliesToFees: rule.appliesToFees ?? true,
        taxAmount: 0,
    }));
};

/**
 * Resolve taxesBreakdown input for totals calculation.
 * Create: build from shop rules when state is empty.
 * Edit: use hydrated state; return [] until GET loads.
 */
export const resolveBreakdownForCalc = ({ isNewTaxVersion = false, id, taxesBreakdown = [], shopTaxesData, documentType = DOCUMENT_TAX_TYPE.INVOICE }) => {
    if (!isNewTaxVersion) return taxesBreakdown;
    if (taxesBreakdown?.length > 0) return taxesBreakdown;
    if (id) return [];
    return buildTaxesBreakdownFromShopTaxes(shopTaxesData, documentType);
};

/**
 * Only sync calculated breakdown when rows exist (never wipe with []).
 */
export const shouldSyncCalculatedBreakdown = (calculatedBreakdown) => Array.isArray(calculatedBreakdown) && calculatedBreakdown.length > 0;

/**
 * Merge shop rule flags onto breakdown rows (for appliesTo* during calc).
 */
export const mergeShopTaxRulesIntoBreakdown = (taxesBreakdown = [], shopTaxRules = []) => {
    const ruleMap = new Map((shopTaxRules || []).map((r) => [r._id, r]));

    return taxesBreakdown.map((row) => {
        const shopRule = ruleMap.get(row.taxId);
        return {
            ...row,
            appliesToParts: row.appliesToParts ?? shopRule?.appliesToParts ?? true,
            appliesToLabour: row.appliesToLabour ?? shopRule?.appliesToLabour ?? true,
            appliesToServicePackage: row.appliesToServicePackage ?? shopRule?.appliesToServicePackage ?? true,
            appliesToFees: row.appliesToFees ?? shopRule?.appliesToFees ?? true,
        };
    });
};

/**
 * Pro-rate discount across subtotal components.
 */
export const computeComponentNets = ({ labourTotal = 0, partsTotal = 0, packagesTotal = 0, fees = 0, discount = 0, subTotal = 0 }) => {
    const labour = Number(labourTotal) || 0;
    const parts = Number(partsTotal) || 0;
    const packages = Number(packagesTotal) || 0;
    const feesNum = Number(fees) || 0;
    const discountNum = Number(discount) || 0;
    const sub = Number(subTotal) || labour + parts + packages;

    if (sub <= 0) {
        return { labourNet: labour, partsNet: parts, packagesNet: packages, feesNet: feesNum };
    }

    const ratio = (part, total) => NumericEngine.divide(Raw.mul(String(part), String(discountNum)), String(total));

    return {
        labourNet: Number(NumericEngine.subtract(String(labour), ratio(labour, sub))),
        partsNet: Number(NumericEngine.subtract(String(parts), ratio(parts, sub))),
        packagesNet: Number(NumericEngine.subtract(String(packages), ratio(packages, sub))),
        feesNet: feesNum,
    };
};

/**
 * Calculate per-line tax for multi-tax mode.
 */
export const calculateMultiTaxLines = ({
    taxesBreakdown = [],
    labourTotal = 0,
    partsTotal = 0,
    packagesTotal = 0,
    fees = 0,
    discount = 0,
    subTotal = 0,
    documentType = DOCUMENT_TAX_TYPE.INVOICE,
}) => {
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    const { labourNet, partsNet, packagesNet, feesNet } = computeComponentNets({
        labourTotal,
        partsTotal,
        packagesTotal,
        fees,
        discount,
        subTotal,
    });

    const updatedBreakdown = taxesBreakdown.map((row) => {
        if (!row.applyTax) {
            return { ...row, taxAmount: 0 };
        }

        let ruleBase = '0';

        if (row.appliesToLabour) {
            ruleBase = NumericEngine.add(ruleBase, String(labourNet));
        }

        if (row.appliesToServicePackage) {
            ruleBase = NumericEngine.add(ruleBase, String(packagesNet));
        }

        if (row.appliesToParts) {
            ruleBase = NumericEngine.add(ruleBase, String(partsNet));
        }
        if (row.appliesToFees) {
            ruleBase = NumericEngine.add(ruleBase, String(feesNet));
        }

        const rate = Number(row[rateKey]) || 0;
        const lineTax = rate > 0 ? NumericEngine.multiply(Raw.div(rate, 100), ruleBase) : '0';

        return {
            ...row,
            taxAmount: Number(lineTax),
        };
    });

    const totalTax = updatedBreakdown.reduce((acc, row) => NumericEngine.add(acc, String(row.taxAmount || 0)), '0');

    let totalTaxableBase = '0';
    updatedBreakdown.forEach((row) => {
        if (!row.applyTax) return;
        if (row.appliesToServicePackage) {
            totalTaxableBase = NumericEngine.add(totalTaxableBase, String(packagesNet));
        }
        if (row.appliesToLabour) {
            totalTaxableBase = NumericEngine.add(totalTaxableBase, String(labourNet));
        }
        if (row.appliesToParts) {
            totalTaxableBase = NumericEngine.add(totalTaxableBase, String(partsNet));
        }
        if (row.appliesToFees) {
            totalTaxableBase = NumericEngine.add(totalTaxableBase, String(feesNet));
        }
    });

    const taxableNum = Number(totalTaxableBase) || 0;
    const taxNum = Number(totalTax) || 0;
    const effectiveTaxPercentage = taxableNum > 0 ? Number(NumericEngine.divide(Raw.mul(taxNum, 100), taxableNum)) : 0;

    return {
        taxesBreakdown: updatedBreakdown,
        tax: taxNum,
        taxPercentage: effectiveTaxPercentage.toString(),
        totalTaxableBase: taxableNum,
    };
};

// ---------------------------------------------------------------------------
// Per-line-item taxes (new multi-tax version only)
// Each taxable line (labour / parts / fixed service-package / fee) carries its
// own taxesBreakdown: [{ taxId, taxName, applyTax }]. Defaults come from the
// shop rule's appliesTo<kind> flag; the user can override per line.
// ---------------------------------------------------------------------------

/** Line kind -> shop-rule appliesTo flag used for the default + fallback. */
export const LINE_TAX_KIND = {
    LABOUR: 'labour',
    PARTS: 'parts',
    PACKAGE: 'package',
    FEES: 'fees',
};

const LINE_APPLIES_KEY = {
    [LINE_TAX_KIND.LABOUR]: 'appliesToLabour',
    [LINE_TAX_KIND.PARTS]: 'appliesToParts',
    [LINE_TAX_KIND.PACKAGE]: 'appliesToServicePackage',
    [LINE_TAX_KIND.FEES]: 'appliesToFees',
};

/**
 * Default per-line taxesBreakdown for a newly added line, seeded from shop rules.
 * applyTax defaults to the rule's appliesTo<kind> flag.
 */
export const buildLineDefaultTaxes = (shopTaxesData, kind) => {
    const key = LINE_APPLIES_KEY[kind];
    return getShopTaxRules(shopTaxesData).map((rule) => ({
        taxId: rule._id,
        taxName: rule.taxName,
        applyTax: (key ? rule[key] : undefined) ?? true,
    }));
};

/** Gross of an editor-state part row: qty × (cost + per-unit fee). */
export const partGrossAmount = (part) => {
    const qty = Number(part?.partQty) || 0;
    const cost = Number(part?.partCost) || 0;
    const fee = Number(part?.feeAmount) || 0;
    return qty * cost + qty * fee;
};

/**
 * Gross of a SAVED (GET response) part row — the read-side twin of partGrossAmount().
 *
 * The backend stores `totalAmount` as qty × cost ONLY; a managed part's per-unit fee ("Parts
 * disposal") is a separate `feeAmount` field, rendered on its own row. The money the customer pays
 * for the part — and therefore the amount tax is charged on — is qty × (cost + fee), exactly as
 * partGrossAmount() computes it in the editor. Reading `totalAmount` alone under-taxed every part
 * that carries a fee on the detail pages and in the record-payment dialog.
 *
 * Field names differ from the editor's: totalQty / cost / feeAmount (with the editor's names
 * accepted as fallbacks, since some rows are hydrated into editor shape before reaching here).
 */
export const recordPartGrossAmount = (part) => {
    const qty = Number(part?.totalQty ?? part?.qty ?? part?.partQty) || 0;
    const fee = Number(part?.feeAmount) || 0;
    const stored = part?.totalAmount;
    const base =
        stored !== undefined && stored !== null && stored !== '' ? Number(stored) || 0 : qty * (Number(part?.cost ?? part?.retailPrice ?? part?.partCost) || 0);
    return base + qty * fee;
};

/**
 * A REAL part is one worth money — a positive amount. That is the ONLY test, deliberately: a part
 * the user has priced but not yet named ($100, no name, unmanaged search row) is real money and must
 * be taxed, exactly as production does. Requiring a name here was a regression — it dropped that row
 * from the summary while the inline chip still taxed it.
 *
 * Amount alone still filters out the two things it should: a blank row the user opened and never
 * filled, and a $0 "ghost" row — both have amount 0.
 */
export const isRealPart = (part) => partGrossAmount(part) > 0;

/**
 * The account-level default for a line kind under one shop tax rule — the ONLY thing that decides
 * whether a newly added line is taxed. Labour is treated exactly like parts: read the rule's
 * appliesTo<kind> flag, apply it, done. No labour-only detection, no suppression, no locking.
 */
export const getLineTaxAccountDefault = (rule, kind) => {
    const key = LINE_APPLIES_KEY[kind];
    return (key ? rule?.[key] : undefined) ?? true;
};

/**
 * `customChange` — true when this line's tax no longer matches the account-level default.
 * Purely a comparison of the two, so toggling a value off and back on lands on false again
 * without any extra bookkeeping.
 */
export const isLineTaxCustomChanged = (applyTax, accountDefault) => !!applyTax !== !!accountDefault;

// ---------------------------------------------------------------------------
// Province tax groups
//
// A shop belongs to exactly one group, by province/state code. The ONLY group whose Labour taxability
// depends on document composition is FLORIDA. GROUP_B differs only in tooltip wording; DEFAULT is the
// plain, unchanged behavior. To move a province between groups later, edit these arrays only — every
// consumer resolves through getProvinceTaxGroup(), so nothing else changes.
// ---------------------------------------------------------------------------
export const PROVINCE_TAX_GROUP = { FLORIDA: 'florida', GROUP_B: 'groupB', DEFAULT: 'default' };

export const FLORIDA_PROVINCE_CODES = ['FL'];

export const GROUP_B_PROVINCE_CODES = ['AL', 'CA', 'CO', 'GA', 'ID', 'IL', 'IN', 'KY', 'MD', 'MA', 'MI', 'MO', 'NV', 'OK', 'RI', 'TX', 'VT', 'VA'];

/** Resolve a province/state code to its tax group. Unknown/blank → DEFAULT (unchanged behavior). */
export const getProvinceTaxGroup = (provinceCode) => {
    const code = String(provinceCode || '')
        .trim()
        .toUpperCase();
    if (FLORIDA_PROVINCE_CODES.includes(code)) return PROVINCE_TAX_GROUP.FLORIDA;
    if (GROUP_B_PROVINCE_CODES.includes(code)) return PROVINCE_TAX_GROUP.GROUP_B;
    return PROVINCE_TAX_GROUP.DEFAULT;
};

/**
 * Does the WHOLE document carry a taxable Part or Service Package? Fees are deliberately ignored — a
 * document of Labour + Fees is still "labour only" for the Florida rule. Editor-state variant.
 */
export const editorHasPartsOrPackages = (serviceListPayload = [], PRICING_TYPES) =>
    (serviceListPayload || []).some((s) => (s?.pricingType === PRICING_TYPES?.FXIED ? (Number(s?.fixedPrice) || 0) > 0 : (s?.parts || []).some(isRealPart)));

/** Same question, from a saved GET record (services[].fixedPrice / services[].parts[].totalAmount). */
export const recordHasPartsOrPackages = (record) =>
    (record?.services || []).some((s) =>
        s?.pricingType === 'fixed' ? (Number(s?.fixedPrice) || 0) > 0 : (s?.parts || []).some((p) => (Number(p?.totalAmount) || 0) > 0)
    );

/**
 * THE Florida rule. In a FL shop, Labour is non-taxable while the whole document is labour-only (no
 * parts, no service packages — fees don't count). Add any part/package and Labour becomes taxable
 * again. This is a line-level flag (FL + no parts/packages); whether it actually removes a given tax
 * is decided per-rule by isFloridaLabourRuleSuppressed, so an appliesToLabour=false rule keeps its
 * normal soft behavior. Every other province returns false here — no document-level Labour logic.
 */
export const isFloridaLabourOnlySuppressed = (provinceTaxGroup, hasPartsOrPackages) => provinceTaxGroup === PROVINCE_TAX_GROUP.FLORIDA && !hasPartsOrPackages;

/**
 * Whether the Florida labour-only rule removes THIS tax from THIS line. Only labour lines flagged
 * flLabourSuppressed, and only for rules that would otherwise tax labour (appliesToLabour === true).
 * appliesToLabour === false rules are untouched — they stay soft and user-toggleable, exactly as
 * outside Florida.
 */
export const isFloridaLabourRuleSuppressed = (line, taxRow) =>
    line?.kind === LINE_TAX_KIND.LABOUR && !!line?.flLabourSuppressed && (taxRow?.[LINE_APPLIES_KEY[LINE_TAX_KIND.LABOUR]] ?? true) === true;

/**
 * Chip status for a line's taxesBreakdown: 'T' (all applied), 'N/T' (none), 'P/T' (some).
 */
export const getLineTaxStatus = (lineTaxesBreakdown = []) => {
    const rows = Array.isArray(lineTaxesBreakdown) ? lineTaxesBreakdown : [];
    if (rows.length === 0) return 'N/T';
    const applied = rows.filter((t) => t.applyTax).length;
    if (applied === 0) return 'N/T';
    if (applied === rows.length) return 'T';
    return 'P/T';
};

/**
 * Build save payload for a line's taxesBreakdown:
 * [{ taxId, taxName, [rateKey], applyTax, customChange }].
 * Rate + name are resolved from the shop rules (source of truth).
 * When `globalBreakdown` is passed, applyTax is baked to the EFFECTIVE value
 * (global master gate AND the line's own applyTax) so the backend, summing lines,
 * always matches the UI regardless of how it treats the global row.
 *
 * `customChange` compares the line's OWN applyTax against the account default for `kind` — not the
 * effective value. A tax switched off by the global master gate is not a per-line customisation, so
 * it stays false. Recomputed on every save, so it self-heals if the account default later changes.
 */
export const buildLineTaxesBreakdownPayload = (
    lineTaxesBreakdown = [],
    shopTaxesData,
    documentType = DOCUMENT_TAX_TYPE.INVOICE,
    globalBreakdown = null,
    kind = null
) => {
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    const ruleMap = new Map(getShopTaxRules(shopTaxesData).map((r) => [r._id, r]));
    const globalMap = globalBreakdown ? new Map(globalBreakdown.map((r) => [r.taxId, r])) : null;

    return (lineTaxesBreakdown || []).map((t) => {
        const rule = ruleMap.get(t.taxId);
        const g = globalMap ? globalMap.get(t.taxId) : null;
        // Name + rate follow the current global row (user-editable) first, then the shop rule.
        return {
            taxId: t.taxId,
            taxName: t.taxName ?? t.name ?? g?.taxName ?? g?.name ?? rule?.taxName ?? '',
            // Global row owns the rate (user-editable in the payment summary); line/shop are fallbacks.
            [rateKey]: Number(g?.[rateKey] ?? t[rateKey] ?? rule?.[rateKey] ?? anyRowRate(g) ?? anyRowRate(t) ?? anyRowRate(rule)) || 0,
            // Per line is the source of truth — save the line's OWN value. The payment-summary row is a
            // bulk toggle, not a master gate, so there is nothing to AND against.
            applyTax: !!t.applyTax,
            // customChange is a STORED flag ("user hand-adjusted this line from the line item"), set by
            // the inline popover (own !== account default) and cleared by the payment-summary bulk
            // toggle. Preserve it verbatim; only fall back to a computed value for older data that never
            // carried the flag. Backend stores + returns it as-is.
            customChange: t?.customChange != null ? !!t.customChange : isLineTaxCustomChanged(t.applyTax, getLineTaxAccountDefault(rule, kind)),
        };
    });
};

/**
 * Bake the Florida labour-only rule into a LABOUR line's save payload. When the whole document is
 * labour-only in a FL shop, every rule that would tax labour (appliesToLabour === true) is forced
 * off — and marked customChange:false, since it's a system rule, not a user override. So the saved
 * record already encodes the suppression, and every read path (detail, record-payment) stays
 * consistent without re-running the Florida logic. No-op when flSuppressed is false.
 */
export const applyFloridaLabourSuppression = (labourPayload = [], shopTaxesData, flSuppressed = false) => {
    if (!flSuppressed) return labourPayload;
    const ruleMap = new Map(getShopTaxRules(shopTaxesData).map((r) => [r._id, r]));
    return (labourPayload || []).map((t) => {
        const appliesToLabour = ruleMap.get(t.taxId)?.appliesToLabour ?? true;
        return appliesToLabour ? { ...t, applyTax: false, customChange: false } : t;
    });
};

/**
 * The read-side inverse of applyFloridaLabourSuppression() — used when a SAVED labour line is loaded
 * back into the EDITOR (edit, duplicate, estimate→invoice conversion).
 *
 * The Florida labour-only rule is baked into the save payload as a plain per-line value
 * (`applyTax:false, customChange:false`). On create that suppression is only a live mask over the
 * account default, so adding a part lifts it and labour becomes taxable again. Re-loading the saved
 * line copied the baked `false` in as an EXPLICIT per-line choice, and an explicit choice beats the
 * account default in lineTaxApplies() — so adding a part in EDIT left labour stuck on N/T.
 *
 * So restore `applyTax:true` on exactly the entries the bake could have produced, and let the live
 * Florida rule mask them again while the document is still labour-only.
 *
 * NOT keyed on `customChange:false` alone. That flag does NOT mean "equals the account default": the
 * payment-summary bulk uncheck (setLineTaxApplyForId) also writes `applyTax:false, customChange:false`,
 * deliberately, so a bulk action never lights the "Manually Adjusted" badge. Keying on it alone
 * resurrected taxes the user had switched off from the summary — e.g. a BC estimate with PST unchecked
 * everywhere came back with PST on labour. Hence the province + labour-only + appliesToLabour guards
 * below, which together match the bake and nothing else.
 *
 * Only reached while the record PRESERVES its tax (account settings unchanged since it was saved);
 * a reset already rebuilds every line from the account defaults.
 */
export const rehydrateLabourLineTaxes = (
    labourTaxesBreakdown,
    shopTaxesData,
    { provinceTaxGroup = PROVINCE_TAX_GROUP.DEFAULT, hasPartsOrPackages = true } = {}
) => {
    if (!Array.isArray(labourTaxesBreakdown) || labourTaxesBreakdown.length === 0) return labourTaxesBreakdown;
    // ONLY a Florida labour-only record can carry the bake. Everywhere else the stored value is a real
    // decision — including a payment-summary bulk uncheck, which also writes customChange:false — and
    // must survive untouched.
    if (!isFloridaLabourOnlySuppressed(provinceTaxGroup, hasPartsOrPackages)) return labourTaxesBreakdown;

    const rules = getShopTaxRules(shopTaxesData);
    // Shop taxes not loaded yet (the invoice edit hydration does not await them): with no rules every
    // account default would read as `true` and switch labour ON for an appliesToLabour=false shop.
    // Keep the saved values instead — the same safe default isTaxSettingsChangedSince() takes.
    if (rules.length === 0) return labourTaxesBreakdown;
    const ruleMap = new Map(rules.map((r) => [r._id, r]));

    return labourTaxesBreakdown.map((t) => {
        // Mirror applyFloridaLabourSuppression() exactly: it only ever forced OFF the rules that would
        // otherwise tax labour (appliesToLabour === true), and only with customChange:false. On a
        // Florida labour-only document those rules are also the ones the summary checkbox cannot
        // touch — applyGlobalTaxToggle() skips suppressed labour — so this combination can have come
        // from nothing but the bake.
        const appliesToLabour = getLineTaxAccountDefault(ruleMap.get(t.taxId), LINE_TAX_KIND.LABOUR);
        const isBake = appliesToLabour === true && t?.applyTax === false && !t?.customChange;
        return isBake ? { ...t, applyTax: true } : t;
    });
};

/**
 * Set ONE tax's applyTax on a single line's breakdown, for the payment-summary BULK toggle.
 * CHECK restores the account default (on only where appliesTo<kind> is true); UNCHECK forces off.
 * customChange is cleared to false either way — a summary action is not a per-line manual adjustment,
 * so it must never light the "Manually Adjusted" badge. Builds default entries from the shop rules
 * when the line has none yet.
 */
export const setLineTaxApplyForId = (lineTaxesBreakdown, taxId, checked, shopTaxesData, kind) => {
    const base = Array.isArray(lineTaxesBreakdown) && lineTaxesBreakdown.length ? lineTaxesBreakdown : buildLineDefaultTaxes(shopTaxesData, kind);
    const ruleMap = new Map(getShopTaxRules(shopTaxesData).map((r) => [r._id, r]));
    return base.map((t) => {
        if (t.taxId !== taxId) return t;
        const accountDefault = getLineTaxAccountDefault(ruleMap.get(t.taxId), kind);
        const target = checked ? accountDefault : false;
        return { ...t, applyTax: target, customChange: false };
    });
};

/**
 * Payment-summary bulk toggle for one tax across the whole document. Materialises the change onto
 * every taxable line (fixed/labour/parts/fees) so per-line state stays the source of truth. Florida-
 * suppressed labour is left untouched (kept baked off). Returns fresh serviceListPayload + selectedFees.
 */
export const applyGlobalTaxToggle = ({
    serviceListPayload = [],
    selectedFees = [],
    taxId,
    checked,
    shopTaxesData,
    provinceTaxGroup = PROVINCE_TAX_GROUP.DEFAULT,
    PRICING_TYPES,
}) => {
    const flLabourSuppressed = isFloridaLabourOnlySuppressed(provinceTaxGroup, editorHasPartsOrPackages(serviceListPayload, PRICING_TYPES));

    const nextServices = (serviceListPayload || []).map((service) => {
        if (service.pricingType === PRICING_TYPES.FXIED) {
            return { ...service, taxesBreakdown: setLineTaxApplyForId(service.taxesBreakdown, taxId, checked, shopTaxesData, LINE_TAX_KIND.PACKAGE) };
        }
        if (service.pricingType === PRICING_TYPES.LABOUR) {
            const next = { ...service };
            // Florida labour-only suppression stays put — the toggle never turns it on or off.
            if (!flLabourSuppressed) {
                next.taxesBreakdown = setLineTaxApplyForId(service.taxesBreakdown, taxId, checked, shopTaxesData, LINE_TAX_KIND.LABOUR);
            }
            next.parts = (service.parts || []).map((part) => ({
                ...part,
                taxesBreakdown: setLineTaxApplyForId(part.taxesBreakdown, taxId, checked, shopTaxesData, LINE_TAX_KIND.PARTS),
            }));
            return next;
        }
        return service;
    });

    const nextFees = (selectedFees || []).map((fee) => ({
        ...fee,
        taxesBreakdown: setLineTaxApplyForId(fee.taxesBreakdown, taxId, checked, shopTaxesData, LINE_TAX_KIND.FEES),
    }));

    return { serviceListPayload: nextServices, selectedFees: nextFees };
};

/**
 * Discount-adjusted net for each taxable line — THE single definition of "the amount tax is
 * charged on". Every taxable base in the app must come from here: the live calc
 * (calculateMultiTaxFromLines), the editor's hover summary (summarizeTaxAcrossLines) and the
 * detail pages' hover summary (summarizeTaxFromRecord). They used to each own a copy, and the
 * record one silently omitted the discount — so the same invoice showed a different taxable
 * amount before and after saving.
 *
 * The document discount is pro-rated across the lines by each line's share of the subtotal
 * (`gross × discount / subTotal`), NOT subtracted once from the total. Fees are discount-exempt
 * (`discountEligible: false`), so they are taxed on their full amount.
 *
 * Takes lines from either buildTaxableLines() (live editor state) or buildTaxableLinesFromRecord()
 * (a saved GET record) — both emit { gross, discountEligible }. Returns nets index-aligned to lines.
 */
export const lineNetAmount = ({ gross = 0, discount = 0, subTotal = 0, discountEligible = true }) => {
    const grossNum = Number(gross) || 0;
    const discountNum = Number(discount) || 0;
    const subNum = Number(subTotal) || 0;

    if (!discountEligible || subNum <= 0 || discountNum <= 0) return grossNum;
    const share = Number(NumericEngine.divide(Raw.mul(String(grossNum), discountNum), String(subNum)));
    return Number(NumericEngine.subtract(String(grossNum), String(share)));
};

/** Nets for a whole line list, index-aligned. Same rule as lineNetAmount(), applied per line. */
export const proratedNets = (lines = [], discount = 0, subTotal = 0) =>
    lines.map((line) => lineNetAmount({ gross: line.gross, discount, subTotal, discountEligible: line.discountEligible }));

/** Fees sit outside the discount base; every other line kind is discount-eligible. */
export const isLineKindDiscountEligible = (kind) => kind !== LINE_TAX_KIND.FEES;

/**
 * Per-tax summary across all taxable lines — drives the payment-summary tri-state
 * checkbox + hover card ("Applicable items X of N", taxable amount, rate, total).
 *
 * Effective per line = globalApply[T] AND (line explicit ?? appliesTo<kind>).
 *
 * A tax row is "customized" — which locks its global checkbox — only when a line REMOVES a tax the
 * shop default applied to it (appliesTo<kind> true, line explicit false) while the global gate is
 * on. Unchecking the global row would then be ambiguous: it cannot express "off everywhere except
 * the lines the user already opted out of", so the user is directed to the line items instead.
 * The opposite override — a line ADDING a tax the shop default excluded — does not lock: the global
 * row is still a clean master gate over every line the tax now applies to, so it stays toggleable.
 */
/**
 * Per-tax summary from ALREADY-BUILT taxable lines. Same logic used by the editor
 * (summarizeTaxAcrossLines) and the record-payment dialog, so both derive the "edited from individual
 * lines" lock (`customized`) identically. Keep this the single source of that computation.
 */
export const summarizeTaxRows = ({ lines = [], taxesBreakdown = [], discount = 0, subTotal = 0, documentType = DOCUMENT_TAX_TYPE.INVOICE }) => {
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    const nets = proratedNets(lines, discount, subTotal);

    return taxesBreakdown.map((row) => {
        const rate = Number(row[rateKey]) || 0;
        let appliedLines = 0;
        let applicableLines = 0;
        let offDefaultCount = 0;
        let taxable = '0';
        let taxAcc = '0';

        lines.forEach((line, i) => {
            // Florida-suppressed labour is never a candidate — out of numerator AND applicable count,
            // but still in totalLines (it is a taxable line on the document).
            if (isFloridaLabourRuleSuppressed(line, row)) return;
            applicableLines += 1;
            const appliesDefault = row[LINE_APPLIES_KEY[line.kind]] ?? true;
            const explicit = Array.isArray(line.taxesBreakdown) ? line.taxesBreakdown.find((t) => t.taxId === row.taxId) : undefined;
            // Per line is the source of truth — the summary is a bulk toggle, not a master gate.
            const lineOwn = explicit ? !!explicit.applyTax : appliesDefault;

            if (lineOwn) {
                appliedLines += 1;
                taxable = NumericEngine.add(taxable, String(nets[i]));
                // Per-line truth: round each line's tax, then sum (matches calculateMultiTaxFromLines).
                if (rate > 0) taxAcc = NumericEngine.add(taxAcc, NumericEngine.multiply(Raw.div(rate, 100), String(nets[i])));
            }
            // A line whose applied state no longer matches its account default. Distinct from the
            // "Manually Adjusted" badge (customChange): a bulk summary-off leaves lines off-default here
            // too, which is what makes a partial re-check read as "mixed" and lock the checkbox.
            if (lineOwn !== appliesDefault) offDefaultCount += 1;
        });

        const taxAmount = Number(taxAcc);
        // Lock the summary checkbox only for a genuine mixed state: partially applied AND at least one
        // line differs from its account default. All-on, all-off, and the plain account default
        // distribution stay toggleable so the user can still bulk set them.
        const customized = appliedLines > 0 && appliedLines < applicableLines && offDefaultCount > 0;

        return {
            taxId: row.taxId,
            name: row.name,
            rate,
            appliedLines,
            // Every taxable line on the document counts, whether or not this tax reaches it.
            totalLines: lines.length,
            taxableAmount: Number(taxable),
            taxAmount,
            customized,
        };
    });
};

/**
 * Per-tax summary across all taxable lines — drives the payment-summary tri-state checkbox + hover
 * card. Builds the taxable lines from the live editor state, then delegates to summarizeTaxRows.
 */
export const summarizeTaxAcrossLines = ({
    serviceListPayload = [],
    selectedFees = [],
    PRICING_TYPES,
    taxesBreakdown = [],
    discount = 0,
    subTotal = 0,
    documentType = DOCUMENT_TAX_TYPE.INVOICE,
    provinceTaxGroup = PROVINCE_TAX_GROUP.DEFAULT,
}) => {
    const lines = buildTaxableLines({ serviceListPayload, selectedFees, PRICING_TYPES, subTotal, provinceTaxGroup });
    return summarizeTaxRows({ lines, taxesBreakdown, discount, subTotal, documentType });
};

/**
 * Whether a global tax row applies to a taxable line.
 * Florida labour-only suppression wins outright for the rules it covers (a hard, non-overridable
 * rule); otherwise the per-line override wins; else fall back to the shop rule's appliesTo<kind> flag.
 */
const lineTaxApplies = (line, taxRow) => {
    if (isFloridaLabourRuleSuppressed(line, taxRow)) return false;
    const lineTaxes = line.taxesBreakdown;
    if (Array.isArray(lineTaxes) && lineTaxes.length > 0) {
        const match = lineTaxes.find((t) => t.taxId === taxRow.taxId);
        if (match) return !!match.applyTax;
    }
    return taxRow[LINE_APPLIES_KEY[line.kind]] ?? true;
};

/**
 * Flatten serviceListPayload + selectedFees into taxable lines, each with its own taxesBreakdown.
 * Labour/parts/package are discount-eligible; fees are not (fees excluded from discount base).
 *
 * Only lines that actually carry money become taxable lines. A $0 labour line (a service billed for
 * parts with no labour hours) and a blank/$0 part row used to be emitted anyway — they contributed
 * no tax but did inflate "Applicable items X of N", and the save payload omits them, so the editor
 * and the saved record disagreed on the count.
 */
export const buildTaxableLines = ({
    serviceListPayload = [],
    selectedFees = [],
    PRICING_TYPES,
    subTotal = '0',
    provinceTaxGroup = PROVINCE_TAX_GROUP.DEFAULT,
}) => {
    const lines = [];
    // Florida: document-level labour-only detection, computed once for the whole document.
    const flLabourSuppressed = isFloridaLabourOnlySuppressed(provinceTaxGroup, editorHasPartsOrPackages(serviceListPayload, PRICING_TYPES));

    (serviceListPayload || []).forEach((service) => {
        if (service.pricingType === PRICING_TYPES.FXIED) {
            const fixedGross = Number(service.fixedPrice) || 0;
            // if (fixedGross <= 0) return; // ❌ drops $0 package
            lines.push({
                kind: LINE_TAX_KIND.PACKAGE,
                gross: fixedGross,
                taxesBreakdown: service.taxesBreakdown,
                discountEligible: true,
            });
        } else if (service.pricingType === PRICING_TYPES.LABOUR) {
            const qty = Number(service.qty) || 0;
            const rate = Number(service.ratePerHour) || 0;
            const laborGross = Number(service.amount != null && service.amount !== '' ? service.amount : qty * rate) || 0;

            if (laborGross > 0) {
                // ❌ drops $0 labour
                lines.push({
                    kind: LINE_TAX_KIND.LABOUR,
                    gross: laborGross,
                    taxesBreakdown: service.taxesBreakdown,
                    discountEligible: true,
                    flLabourSuppressed,
                });
            }

            (service.parts || [])
                // filter(isRealPart).// ❌ drops $0 parts
                .forEach((part) => {
                    lines.push({
                        kind: LINE_TAX_KIND.PARTS,
                        gross: partGrossAmount(part),
                        taxesBreakdown: part.taxesBreakdown,
                        discountEligible: true,
                    });
                });
        }
    });

    (selectedFees || []).forEach((fee) => {
        let feeGross = 0;
        if (fee.calculationType === 'fixed_amount') {
            feeGross = Number(fee.amount) || 0;
        } else {
            const pct = Number(fee.percentage) || 0;
            feeGross = Number(NumericEngine.divide(Raw.mul(String(subTotal), pct), 100));
        }
        lines.push({
            kind: LINE_TAX_KIND.FEES,
            gross: feeGross,
            taxesBreakdown: fee.taxesBreakdown,
            discountEligible: false,
        });
    });

    return lines;
};

/**
 * Same taxable lines as buildTaxableLines(), but derived from a saved GET record
 * (services[].labor / services[].parts / fixed services / fees). Used by the detail-page summaries
 * and the record-payment dialog, so they recalculate with the same per-line engine as the editor.
 */
export const buildTaxableLinesFromRecord = (record) => {
    const lines = [];

    (record?.services || []).forEach((s) => {
        if (s?.pricingType === 'fixed') {
            const fixedGross = Number(s.fixedPrice) || 0;
            // if (fixedGross <= 0) return; // ❌ drops $0 package
            lines.push({
                kind: LINE_TAX_KIND.PACKAGE,
                gross: fixedGross,
                taxesBreakdown: s.taxesBreakdown,
                discountEligible: true,
            });
        } else {
            const labourGross = Number(s?.labor?.totalAmount) || 0;
            // if (s?.labor && labourGross > 0) { // ❌ drops $0 labour
            if (s.labor) {
                lines.push({
                    kind: LINE_TAX_KIND.LABOUR,
                    gross: labourGross,
                    taxesBreakdown: s.labor.taxesBreakdown,
                    discountEligible: true,
                });
            }
            // }
            (s?.parts || []).forEach((p) => {
                // qty × (cost + per-unit fee) — see recordPartGrossAmount.
                const partGross = recordPartGrossAmount(p);
                // if (partGross <= 0) return; // ❌ drops $0 parts
                lines.push({
                    kind: LINE_TAX_KIND.PARTS,
                    gross: partGross,
                    taxesBreakdown: p.taxesBreakdown,
                    discountEligible: true,
                });
            });
        }
    });

    (record?.fees || []).forEach((f) => {
        const feeGross = Number(f.feesAmount) || 0;
        // if (feeGross <= 0) return; // ❌ drops $0 fees
        lines.push({
            kind: LINE_TAX_KIND.FEES,
            gross: feeGross,
            taxesBreakdown: f.taxesBreakdown,
            discountEligible: false,
        });
    });

    return lines;
};

/**
 * Per-line multi-tax calc. PER-LINE IS THE SOURCE OF TRUTH — there is NO master gate. The summary
 * row's applyTax is DERIVED here as "any applicable line has this tax on", so the checkbox reflects
 * the lines rather than gating them.
 *
 * Each applicable line's tax is rounded to 2dp and then summed
 *   taxAmount_T = Σ round2(net_i × rate_T)
 * rather than rounding once over the summed base (round2(Σ net_i × rate_T)). Those two are
 * different functions and can differ by a cent, so the per-tax total shown in the payment summary
 * is exactly the sum of the per-line amounts shown in the chips/hovers — by construction.
 */
export const calculateMultiTaxFromLines = ({ taxesBreakdown = [], lines = [], discount = 0, subTotal = 0, documentType = DOCUMENT_TAX_TYPE.INVOICE }) => {
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    const nets = proratedNets(lines, discount, subTotal);

    const updatedBreakdown = taxesBreakdown.map((row) => {
        const rate = Number(row[rateKey]) || 0;
        // Round each applicable line's tax to 2dp (NumericEngine.multiply finalizes at 2dp),
        // then sum — so this per-tax total equals the sum of the per-line amounts displayed.
        let taxAcc = '0';
        // Per-tax taxable base: the nets of the lines THIS tax reaches. Each rule has its own base
        // (a rule with appliesToParts=false sees a smaller one), so it belongs on the row, not the
        // document. Same number the hover card shows — and the one to send as `taxableAmount` when
        // the payload gains that key.
        let taxableAcc = '0';
        let anyOn = false;
        lines.forEach((line, i) => {
            if (!lineTaxApplies(line, row)) return;
            anyOn = true;
            taxableAcc = NumericEngine.add(taxableAcc, String(nets[i]));
            if (rate > 0) taxAcc = NumericEngine.add(taxAcc, NumericEngine.multiply(Raw.div(rate, 100), String(nets[i])));
        });

        return { ...row, applyTax: anyOn, taxAmount: Number(taxAcc), taxableAmount: Number(taxableAcc) };
    });

    const totalTax = updatedBreakdown.reduce((acc, row) => NumericEngine.add(acc, String(row.taxAmount || 0)), '0');

    // Sum of the per-tax bases computed above. With more than one tax rule a line is counted once
    // per rule that reaches it, so this is NOT "the taxable amount of the document" — it is only the
    // denominator for the blended effective rate below.
    const totalTaxableBase = updatedBreakdown.reduce((acc, row) => (row.applyTax ? NumericEngine.add(acc, String(row.taxableAmount || 0)) : acc), '0');

    const taxableNum = Number(totalTaxableBase) || 0;
    const taxNum = Number(totalTax) || 0;
    const effectiveTaxPercentage = taxableNum > 0 ? Number(NumericEngine.divide(Raw.mul(taxNum, 100), taxableNum)) : 0;

    return {
        taxesBreakdown: updatedBreakdown,
        tax: taxNum,
        taxPercentage: effectiveTaxPercentage.toString(),
        totalTaxableBase: taxableNum,
    };
};

/**
 * Build save payload for taxesBreakdown (strip computed fields).
 */
export const buildTaxesBreakdownPayload = (taxesBreakdown = [], documentType = DOCUMENT_TAX_TYPE.INVOICE) => {
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];

    return taxesBreakdown.map((row) => ({
        taxId: row.taxId,
        taxName: row.taxName ?? row.name ?? '',
        applyTax: !!row.applyTax,
        // Read this document's own key first, then whichever key the row was built with.
        [rateKey]: Number(row[rateKey] ?? anyRowRate(row)) || 0,
    }));
};

/**
 * Calculate service subtotals from serviceListPayload.
 */
export const calculateServiceSubtotals = (serviceListPayload = [], PRICING_TYPES) => {
    let totalLaborAmount = '0';
    let totalPartsAmount = '0';
    let totalFixedAmount = '0';

    if (serviceListPayload?.length > 0) {
        totalLaborAmount = serviceListPayload
            .filter((service) => service.pricingType === PRICING_TYPES.LABOUR)
            .reduce((acc, service) => {
                const qty = Number(service.qty) || 0;
                const rate = Number(service.ratePerHour) || 0;
                const laborAmount = service.amount ? NumericEngine.format(service.amount) : qty && rate ? NumericEngine.multiply(qty, rate) : '0';
                return NumericEngine.add(acc, laborAmount);
            }, '0');

        totalPartsAmount = serviceListPayload
            .filter((service) => service.pricingType === PRICING_TYPES.LABOUR && service.parts?.length)
            .flatMap((service) => service.parts || [])
            .reduce((acc, part) => {
                const partQty = Number(part.partQty) || 0;
                const partCost = Number(part.partCost) || 0;
                const partFee = Number(part?.feeAmount) || 0;
                const partCostAmount = partQty && partCost ? NumericEngine.multiply(partQty, partCost) : '0';
                const partFeeAmount = partQty && partFee ? NumericEngine.multiply(partQty, partFee) : '0';
                return NumericEngine.add(NumericEngine.add(acc, partCostAmount), partFeeAmount);
            }, '0');

        totalFixedAmount = serviceListPayload
            .filter((service) => service.pricingType === PRICING_TYPES.FXIED)
            .reduce((acc, service) => {
                const fixedPrice = Number(service.fixedPrice) || 0;
                return NumericEngine.add(acc, fixedPrice);
            }, '0');
    }

    const subTotal = NumericEngine.add(NumericEngine.add(totalLaborAmount, totalPartsAmount), totalFixedAmount);

    return {
        totalLaborAmount,
        totalPartsAmount,
        totalFixedAmount,
        subTotal,
    };
};

/**
 * Calculate fees total from selectedFees.
 */
export const calculateFeesTotal = (selectedFees = [], subTotal = '0') => {
    if (!selectedFees?.length) return '0';

    return selectedFees.reduce((acc, fee) => {
        let feeAmount = '0';
        if (fee.calculationType === 'fixed_amount') {
            feeAmount = String(Number(fee.amount) || 0);
        } else {
            const feePercentage = Number(fee.percentage) || 0;
            feeAmount = NumericEngine.divide(Raw.mul(subTotal, feePercentage), 100);
        }
        return NumericEngine.add(acc, feeAmount);
    }, '0');
};

/**
 * Unified document totals calculator.
 */
export const calculateDocumentTotals = ({
    mode = 'legacy',
    serviceListPayload = [],
    PRICING_TYPES,
    discountId,
    selectedDiscount,
    paymentDetails = {},
    selectedFees = [],
    taxesBreakdown = [],
    documentType = DOCUMENT_TAX_TYPE.INVOICE,
    provinceTaxGroup = PROVINCE_TAX_GROUP.DEFAULT,
}) => {
    const { totalLaborAmount, totalPartsAmount, totalFixedAmount, subTotal } = calculateServiceSubtotals(serviceListPayload, PRICING_TYPES);

    let discountResolved;
    if (discountId === 'none') {
        discountResolved = resolveDiscount({ subTotal, discountSource: DISCOUNT_SOURCE.PERCENTAGE, discountAmount: 0, discountPercentage: 0 });
    } else if (selectedDiscount?._id) {
        discountResolved = resolveDiscount({
            subTotal,
            programPercentage: selectedDiscount?.discount,
        });
    } else {
        discountResolved = resolveDiscount({
            subTotal,
            discountSource: paymentDetails?.discountSource || DISCOUNT_SOURCE.PERCENTAGE,
            discountAmount: paymentDetails?.discount,
            discountPercentage: paymentDetails?.discountPercentage,
        });
    }

    const { discountValue, discountPercentage, discountSource } = discountResolved;
    const totalFeesAmount = calculateFeesTotal(selectedFees, subTotal);

    const labourNum = Number(totalLaborAmount);
    const partsNum = Number(totalPartsAmount);
    const packagesNum = Number(totalFixedAmount);
    const subTotalNum = Number(subTotal);
    const discountNum = Number(discountValue);
    const feesNum = Number(totalFeesAmount);

    if (mode === 'multi') {
        const taxableLines = buildTaxableLines({ serviceListPayload, selectedFees, PRICING_TYPES, subTotal, provinceTaxGroup });
        const multiResult = calculateMultiTaxFromLines({
            taxesBreakdown,
            lines: taxableLines,
            discount: discountNum,
            subTotal: subTotalNum,
            documentType,
        });

        const taxableAmount = NumericEngine.subtract(NumericEngine.add(subTotal, totalFeesAmount), discountValue);
        const total = NumericEngine.add(taxableAmount, String(multiResult.tax));

        return {
            paymentDetails: {
                subTotal: subTotalNum,
                labourTotal: labourNum,
                partsTotal: partsNum,
                packagesTotal: packagesNum,
                discountPercentage: discountPercentage.toString(),
                discount: discountNum,
                discountSource,
                tax: multiResult.tax,
                taxPercentage: multiResult.taxPercentage,
                fees: feesNum,
                total: Number(total),
            },
            taxesBreakdown: multiResult.taxesBreakdown,
            totalTaxableBase: multiResult.totalTaxableBase,
            isValidDiscount: subTotalNum === 0 || discountNum <= subTotalNum,
        };
    }

    const taxableAmount = NumericEngine.subtract(NumericEngine.add(subTotal, totalFeesAmount), discountValue);
    const taxPercentage = Number(paymentDetails?.taxPercentage) || 0;
    const taxAmount = taxPercentage > 0 ? NumericEngine.multiply(Raw.div(taxPercentage, 100), taxableAmount) : '0';
    const total = NumericEngine.add(taxableAmount, taxAmount);

    return {
        paymentDetails: {
            subTotal: subTotalNum,
            labourTotal: labourNum,
            partsTotal: partsNum,
            packagesTotal: packagesNum,
            discountPercentage: discountPercentage.toString(),
            discount: discountNum,
            discountSource,
            tax: Number(taxAmount),
            taxPercentage: paymentDetails?.taxPercentage,
            fees: feesNum,
            total: Number(total),
        },
        taxesBreakdown: null,
        isValidDiscount: subTotalNum === 0 || discountNum <= subTotalNum,
    };
};

/**
 * Zero-tax guard for new multi-tax: warn when taxable base > 0 but no tax applied.
 */
export const shouldWarnZeroMultiTax = (paymentDetails, taxesBreakdown = []) => {
    const subTotal = Number(paymentDetails?.subTotal) || 0;
    const fees = Number(paymentDetails?.fees) || 0;
    const discount = Number(paymentDetails?.discount) || 0;
    const taxableAmount = subTotal + fees - discount;

    if (taxableAmount <= 0) return false;

    const hasAppliedTax = taxesBreakdown.some((row) => row.applyTax && Number(row.taxAmount) > 0);
    return !hasAppliedTax && !Number(paymentDetails?.tax);
};

/**
 * Per-tax summary from a GET detail record (read-only pages) — the read-side twin of
 * summarizeTaxAcrossLines(), and it must agree with it row for row on the same document.
 *
 * Lines come from buildTaxableLinesFromRecord() and the taxable base from proratedNets(), the same
 * two helpers the live calc uses, so the "Applicable X of N / Taxable amount / rate / Total" card
 * reads identically before and after saving. It previously flattened the record itself and summed
 * the GROSS line amounts, which silently dropped the discount: an invoice showing a $711.79 taxable
 * base while being edited showed $791.11 once saved — and $791.11 × the rate did not equal the tax
 * total printed directly beneath it.
 *
 * taxAmount still prefers the STORED value (server-side calculation is authoritative on read
 * pages); only the taxable base is derived here.
 *
 * `customized` here means "applied to some but not all lines" — it is NOT the editor's
 * lock signal (see summarizeTaxAcrossLines); nothing on a read-only page can be locked.
 */
export const summarizeTaxFromRecord = (record, documentType = DOCUMENT_TAX_TYPE.INVOICE) => {
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    const topLevel = Array.isArray(record?.taxesBreakdown) ? record.taxesBreakdown : [];

    const lines = buildTaxableLinesFromRecord(record);
    const nets = proratedNets(lines, record?.discount, record?.subTotal);

    return topLevel.map((row) => {
        let appliedLines = 0;
        let taxable = '0';
        let taxAmt = '0';
        lines.forEach((line, i) => {
            const entry = Array.isArray(line.taxesBreakdown) ? line.taxesBreakdown.find((t) => t.taxId === row.taxId) : null;
            if (entry && entry.applyTax) {
                appliedLines += 1;
                taxable = NumericEngine.add(taxable, String(nets[i]));
                taxAmt = NumericEngine.add(taxAmt, String(entry.taxAmount || 0));
            }
        });
        return {
            taxId: row.taxId,
            name: row.taxName ?? row.name ?? '',
            rate: Number(row.taxPercentage ?? row[rateKey] ?? 0),
            applyTax: row.applyTax !== false,
            appliedLines,
            // Every taxable line on the document counts — labour, parts, packages and fees alike.
            totalLines: lines.length,
            taxableAmount: Number(taxable),
            // Prefer the stored top-level taxAmount; fall back to the summed per-line amounts.
            taxAmount: Number(row.taxAmount != null ? row.taxAmount : taxAmt),
            customized: appliedLines > 0 && appliedLines < lines.length,
        };
    });
};

/**
 * Format tax line label for detail views.
 */
export const formatTaxLineLabel = (name, rate, documentType = DOCUMENT_TAX_TYPE.INVOICE) => {
    const rateKey = RATE_KEY[documentType] || 'invoiceRate';
    const pct = rate ?? 0;
    return `${name} (${pct}%)`;
};

/**
 * Get rate from breakdown row for display.
 */
export const getTaxRowRate = (row, documentType = DOCUMENT_TAX_TYPE.INVOICE) => {
    const rateKey = RATE_KEY[documentType] || RATE_KEY[DOCUMENT_TAX_TYPE.INVOICE];
    return row?.[rateKey] ?? anyRowRate(row) ?? 0;
};
